// /governance — public page (no auth). Renders the AI Use Statement
// markdown as plain HTML with print-friendly styling so a privacy reviewer
// or board member can read, print, or save to PDF via the browser's print
// dialog. Heavy PDF dependencies (puppeteer, pdfkit) are intentionally
// avoided in the Worker bundle; browser print covers the use case.

import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';

interface AiUseStatement {
  version: string;
  content_markdown: string;
}

export default function GovernancePage() {
  const q = useQuery({
    queryKey: ['governance', 'ai-use-statement'],
    queryFn: () => api.get<AiUseStatement>('/governance/ai-use-statement'),
    staleTime: 60_000 * 60, // an hour — public, immutable content
  });

  return (
    <div className="h-full overflow-y-auto bg-white">
      <article className="max-w-3xl mx-auto px-6 py-10 print:px-0 print:py-0">
        <header className="mb-6 print:mb-4">
          <p className="text-xs uppercase tracking-wide text-neutral-500">Public governance document</p>
          <p className="mt-2 text-xs text-neutral-600">
            This page describes how Granted uses artificial intelligence,
            where data is stored, and how the human verification step works.
            It is intentionally readable without login.
          </p>
          <div className="mt-4 flex gap-2 print:hidden">
            <button
              type="button"
              onClick={() => window.print()}
              className="text-xs rounded-md border border-neutral-300 px-2 py-1 hover:bg-neutral-50"
            >
              Print or save as PDF
            </button>
            {q.data && (
              <a
                href="/api/v1/governance/ai-use-statement"
                target="_blank"
                rel="noreferrer"
                className="text-xs rounded-md border border-neutral-300 px-2 py-1 hover:bg-neutral-50"
              >
                Download Markdown
              </a>
            )}
          </div>
        </header>

        {q.isLoading && <p className="text-sm text-neutral-600">Loading governance — a moment.</p>}
        {q.error && <p className="text-sm text-rose-700">Could not load the AI Use Statement.</p>}

        {q.data && (
          <>
            <div
              className="prose-document"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(q.data.data.content_markdown) }}
            />
            <footer className="mt-10 pt-4 border-t border-neutral-200 text-xs text-neutral-500">
              Document version: <span className="font-mono">{q.data.data.version}</span>
            </footer>
          </>
        )}
      </article>

      {/* Print-friendly styles. Inline so this page works even if globals.css
          forgets a @media print rule. Browsers strip the navigation chrome
          when the user picks "Save as PDF". */}
      <style>{`
        .prose-document h1 { font-size: 1.75rem; font-weight: 600; margin: 0 0 1rem; line-height: 1.2; }
        .prose-document h2 { font-size: 1.15rem; font-weight: 600; margin: 1.5rem 0 .5rem; }
        .prose-document p  { margin: .75rem 0; line-height: 1.55; color: #1f2937; }
        .prose-document blockquote {
          border-left: 3px solid #d4d4d8; padding: .25rem 0 .25rem .75rem;
          margin: .75rem 0; color: #525252; font-style: italic;
        }
        .prose-document ul { list-style: disc; padding-left: 1.25rem; margin: .75rem 0; }
        .prose-document ol { list-style: decimal; padding-left: 1.25rem; margin: .75rem 0; }
        .prose-document li { margin: .25rem 0; line-height: 1.5; color: #1f2937; }
        .prose-document strong { font-weight: 600; }
        .prose-document code { font-family: var(--font-mono); background: #f4f4f5; padding: 0 .25rem; border-radius: 3px; }
        @media print {
          header nav, .print\\:hidden { display: none !important; }
          body { background: white !important; }
          .prose-document { font-size: 11pt; color: black; }
          .prose-document h1 { font-size: 18pt; }
          .prose-document h2 { font-size: 13pt; }
          .prose-document blockquote { color: #333; }
          a { color: black; text-decoration: underline; }
        }
      `}</style>
    </div>
  );
}

// Minimal Markdown → HTML renderer. Only supports the constructs used in
// docs/ai-use-statement.md: # / ## headings, blockquotes, ordered and
// unordered lists, and **bold** inline. Anything richer (tables, code
// fences) would require a real parser; the AI Use Statement is plain by
// design so the constraint pushes back against feature creep.
function renderMarkdown(src: string): string {
  const lines = src.split('\n');
  const out: string[] = [];
  let inList: 'ul' | 'ol' | null = null;
  let para: string[] = [];

  const flushPara = () => {
    if (para.length === 0) return;
    out.push(`<p>${inlineMd(para.join(' '))}</p>`);
    para = [];
  };
  const closeList = () => {
    if (inList) { out.push(`</${inList}>`); inList = null; }
  };

  for (const raw of lines) {
    const line = raw.replace(/\s+$/, '');

    if (line === '') {
      flushPara();
      closeList();
      continue;
    }

    let m: RegExpMatchArray | null;
    if ((m = line.match(/^(#{1,6})\s+(.+)$/))) {
      flushPara(); closeList();
      const level = m[1].length;
      out.push(`<h${level}>${inlineMd(m[2])}</h${level}>`);
      continue;
    }
    if ((m = line.match(/^>\s?(.*)$/))) {
      flushPara(); closeList();
      out.push(`<blockquote>${inlineMd(m[1])}</blockquote>`);
      continue;
    }
    if ((m = line.match(/^(\d+)\.\s+(.+)$/))) {
      flushPara();
      if (inList !== 'ol') { closeList(); out.push('<ol>'); inList = 'ol'; }
      out.push(`<li>${inlineMd(m[2])}</li>`);
      continue;
    }
    if ((m = line.match(/^[-*]\s+(.+)$/))) {
      flushPara();
      if (inList !== 'ul') { closeList(); out.push('<ul>'); inList = 'ul'; }
      out.push(`<li>${inlineMd(m[1])}</li>`);
      continue;
    }
    closeList();
    para.push(line);
  }
  flushPara();
  closeList();
  return out.join('\n');
}

function inlineMd(s: string): string {
  return escape(s)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code>$1</code>');
}

function escape(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
